import React from 'react';
import {
  getMealPlanRestrictionLabels,
  type MealPlanRestrictionSource,
} from '@/utils/meal-plan-allergen-warnings';
import { Tag, TagList, type TagVariant } from '@/components/ui/tag';

type MealPlanRestrictionBadgesProps = {
  source: MealPlanRestrictionSource;
  maxVisible?: number;
};

type RestrictionBadge = {
  key: string;
  label: string;
  tone: 'allergen' | 'intolerance';
};

const badgeTone: Record<RestrictionBadge['tone'], { variant: TagVariant; prefix: string }> = {
  allergen: {
    variant: 'danger',
    prefix: 'Allergen',
  },
  intolerance: {
    variant: 'warning',
    prefix: 'Intolerance',
  },
};

export const MealPlanRestrictionBadges: React.FC<MealPlanRestrictionBadgesProps> = ({
  source,
  maxVisible = 3,
}) => {
  const { allergenLabels, intoleranceLabels } = getMealPlanRestrictionLabels(source);
  const badges: RestrictionBadge[] = [
    ...allergenLabels.map((label) => ({ key: `allergen-${label}`, label, tone: 'allergen' as const })),
    ...intoleranceLabels.map((label) => ({ key: `intolerance-${label}`, label, tone: 'intolerance' as const })),
  ];

  if (badges.length === 0) return null;

  const visibleBadges = badges.slice(0, maxVisible);
  const hiddenCount = Math.max(0, badges.length - visibleBadges.length);
  const fullTitle = badges.map((badge) => `${badgeTone[badge.tone].prefix}: ${badge.label}`).join(', ');

  return (
    <TagList
      title={fullTitle}
      gap="xs"
      className="inline-flex"
    >
      {visibleBadges.map((badge) => {
        const tone = badgeTone[badge.tone];
        return (
          <Tag
            key={badge.key}
            variant={tone.variant}
            size="xs"
            maxWidth={132}
            className="font-[Inter,sans-serif] font-bold"
          >
            {`${tone.prefix}: ${badge.label}`}
          </Tag>
        );
      })}
      {hiddenCount > 0 ? (
        <Tag variant="neutral" size="xs" className="font-[Inter,sans-serif] font-bold text-[#667085]">
          {`+${hiddenCount}`}
        </Tag>
      ) : null}
    </TagList>
  );
};
